import axios from 'axios';
import apiClient from '../api/client';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend.saltmarshdigital.com/api';

// Helper function to add a user to an app
const addUserToApp = async (userId: string | number, appId: string | number) => {
  try {
    // First attempt: admin endpoint (requires admin privileges)
    try {
      const response = await apiClient.post(`/apps/${appId}/users`, { userId });
      console.log('User added to app successfully via admin endpoint');
      return response.data;
    } catch (adminError) {
      console.warn('Admin endpoint for adding user to app failed:', adminError);
      
      // Second attempt: use the join endpoint (for self-service)
      try {
        const joinResponse = await apiClient.post(`/apps/${appId}/join`);
        console.log('User added to app successfully via join endpoint');
        return joinResponse.data;
      } catch (joinError) {
        console.error('Join app endpoint failed:', joinError);
        throw joinError;
      }
    }
  } catch (error) {
    console.error('All attempts to add user to app failed:', error);
    throw error;
  }
};

// Helper function to assign a role to a user
const assignRoleToUser = async (userId: string | number, roleId: string | number) => {
  try {
    const response = await apiClient.post(`/roles/assign`, { userId, roleId });
    return response.data;
  } catch (error) {
    console.error('Assign role error:', error);
    throw error;
  }
};

export interface ResetPasswordData {
  token: string;
  password: string;
}

interface Role {
  id?: number;
  name: string;
  description?: string;
  permissions?: Record<string, string[]>;
}

// Add this before the authService object
const getUserRoles = async () => {
  try {
    const response = await apiClient.get('/users/roles');
    return response.data || [];
  } catch (error) {
    console.error('Error fetching user roles:', error);
    return [];
  }
};

const authService = {
  login: async (usernameOrEmail: string, password: string) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        usernameOrEmail,
        password
      });
      
      // Handle different token structures
      const token = response.data?.tokens?.accessToken || 
                    response.data?.token || 
                    response.data?.data?.token ||
                    response.data?.data?.tokens?.accessToken;
      
      if (token) {
        localStorage.setItem('token', token);
        
        // After setting the token, immediately fetch user info with roles
        try {
          const userData = await authService.getCurrentUser();
          return {
            ...response.data,
            user: userData.user
          };
        } catch (userError) {
          console.error('Error fetching user data after login:', userError);
          // Continue with the login response even if user fetch fails
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },
  
  register: async (name: string, email: string, password: string, appId?: string) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, {
        username: name,
        email,
        password
      });
      
      // Handle different token structures
      const token = response.data?.tokens?.accessToken || 
                   response.data?.token || 
                   response.data?.data?.token ||
                   response.data?.data?.tokens?.accessToken;
      
      if (token) {
        localStorage.setItem('token', token);
        
        // Try to join the app automatically after registration 
        if (appId) {
          try {
            // Use the newly confirmed joinApp endpoint
            await apiClient.post(`/apps/${appId}/join`);
            console.log(`User successfully joined app ${appId} after registration`);
          } catch (joinError) {
            console.error('Error joining app after registration:', joinError);
            // Continue despite error, as the user is registered successfully
          }
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  },
  
  // Add service methods
  addUserToApp,
  assignRoleToUser,
  
  // Direct methods to join an app with different API patterns
  joinApp: async (appId: string | number) => {
    try {
      // Use the confirmed endpoint pattern from the backend
      const response = await apiClient.post(`/apps/${appId}/join`);
      
      // Check for success in the response
      if (response.data?.success) {
        console.log('Successfully joined app:', appId);
        return response.data;
      } else {
        console.error('Join app response indicated failure:', response.data);
        throw new Error(response.data?.error || 'Failed to join app');
      }
    } catch (error) {
      console.error('Failed to join app:', error);
      throw error;
    }
  },
  
  getCurrentUser: async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Use apiClient which already adds the Authorization header
      const response = await apiClient.get(`/users/me`);
      
      // Format the response to ensure consistent data structure
      const userData = response.data?.user || 
                      response.data?.data?.user || 
                      response.data?.data || 
                      response.data;
      
      // Fetch user roles from the dedicated endpoint
      let rolesResponse = await getUserRoles();
      
      // Extract roles from the response
      let roles = [];
      if (Array.isArray(rolesResponse)) {
        roles = rolesResponse;
      } else if (rolesResponse && rolesResponse.roles) {
        roles = rolesResponse.roles;
      }
      
      // Ensure roles is an array and normalize role names
      if (userData) {
        // Initialize roles array from the dedicated endpoint
        let normalizedRoles = [];
        
        if (roles.length > 0) {
          normalizedRoles = roles.map((role: any) => {
            // Handle different role formats
            let roleName = role;
            if (typeof role === 'object') {
              roleName = role.name || role.role || '';
            }
            
            // Convert to lowercase for case-insensitive comparison
            const normalizedRole = String(roleName).toLowerCase();
            if (normalizedRole.includes('admin')) return 'admin';
            if (normalizedRole.includes('editor')) return 'app_editor';
            return 'user';
          });
        } else {
          // Fallback to user data roles if API call failed
          if (typeof userData.roles === 'string') {
            normalizedRoles = [userData.roles];
          } else if (Array.isArray(userData.roles)) {
            normalizedRoles = userData.roles;
          } else if (userData.role) {
            normalizedRoles = Array.isArray(userData.role) ? userData.role : [userData.role];
          } else {
            // Default to 'user' if no roles found
            normalizedRoles = ['user'];
          }
          
          // Normalize these roles too
          normalizedRoles = normalizedRoles.map((role: string) => {
            const normalizedRole = String(role).toLowerCase();
            if (normalizedRole.includes('admin')) return 'admin';
            if (normalizedRole.includes('editor')) return 'app_editor';
            return 'user';
          });
        }
        
        // Ensure superadmin is mapped to admin
        if (normalizedRoles.includes('superadmin')) {
          normalizedRoles = Array.from(new Set([...normalizedRoles, 'admin']));
        }
        
        // Update the user data with normalized roles
        userData.roles = normalizedRoles;
      }
                      
      return { user: userData };
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  },
  
  logout: () => {
    localStorage.removeItem('token');
  },

  // Add password reset methods
  resetPassword: async (email: string) => {
    try {
      const response = await apiClient.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  },

  confirmResetPassword: async (data: ResetPasswordData) => {
    try {
      const response = await apiClient.post('/auth/reset-password', data);
      return response.data;
    } catch (error) {
      console.error('Confirm reset password error:', error);
      throw error;
    }
  }
};

export default authService; 